import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  DriveFileRenameOutline as SignatureIcon,
  TextFields as TextIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Edit as InitialsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

// 将 getFieldIcon 函数移到最前面
const getFieldIcon = (type, color) => {
  switch (type) {
    case 'signature':
      return <SignatureIcon sx={{ color }} />;
    case 'initials':
      return <InitialsIcon sx={{ color }} />;
    case 'name':
      return <PersonIcon sx={{ color }} />;
    case 'date':
      return <DateIcon sx={{ color }} />;
    case 'text':
      return <TextIcon sx={{ color }} />;
    default:
      return null;
  }
};

// 可拖拽的签名字段组件
const DraggableField = ({ type, icon: Icon, label, color }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'field',
    item: { type, label },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Box
      ref={drag}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: `1px solid ${color || '#e0e0e0'}`,
        borderRadius: 1,
        backgroundColor: '#fff',
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
        mb: 1,
      }}
    >
      <Icon sx={{ color: color || 'primary.main' }} />
      <Typography sx={{ color: color }}>
        {label}
      </Typography>
    </Box>
  );
};

const PlacedField = ({ field, onDelete }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'placedField',
    item: { id: field.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [showDelete, setShowDelete] = useState(false);

  return (
    <Box
      ref={drag}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
      sx={{
        position: 'absolute',
        left: field.position.x,
        top: field.position.y,
        padding: '8px 16px',
        border: `2px dashed ${field.color}`,
        borderRadius: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        cursor: 'move',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {getFieldIcon(field.type, field.color)}
      <Typography sx={{ color: field.color }}>
        {field.label}
      </Typography>
      
      {/* 删除按钮 */}
      {showDelete && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(field.id);
          }}
          sx={{
            position: 'absolute',
            top: -12,
            right: -12,
            backgroundColor: '#fff',
            border: `1px solid ${field.color}`,
            padding: '2px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <CloseIcon 
            sx={{ 
              fontSize: 16,
              color: field.color
            }} 
          />
        </IconButton>
      )}
    </Box>
  );
};

const SignaturePositionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const transactionData = location.state?.transactionData || {
    name: 'Insurance Document Signing'
  };

  const [recipients] = useState([
    {
      id: 1,
      name: 'William Chen',
      email: 'william.chen@brokerteam.ca',
      role: 'Insurance Agent'
    },
    {
      id: 2,
      name: 'Emma Thompson',
      email: 'emma.t@email.com',
      role: 'Primary Insured'
    },
    {
      id: 3,
      name: 'James Wilson',
      email: 'james.w@email.com',
      role: 'Additional Insured'
    }
  ]);

  const [documents] = useState([
    {
      id: 1,
      name: 'Electronic Disclosures and Signatures Consent',
      type: 'consent',
      content: `
        ELECTRONIC SIGNATURE CONSENT

        By signing this Electronic Signature Consent document, you agree that:

        1. You consent to use electronic signatures for documents
        2. Your electronic signature is legally binding
        3. You have the necessary hardware and software
        4. You can download and save electronic documents

        This consent applies to all insurance documents in this transaction.
      `
    },
    {
      id: 2,
      name: 'Auto Insurance Quote',
      type: 'quote',
      content: '...'
    },
    {
      id: 3,
      name: 'Insurance Policy Documents',
      type: 'policy',
      content: '...'
    }
  ]);

  const [selectedDocument, setSelectedDocument] = useState(documents[0]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [placedFields, setPlacedFields] = useState([]);

  const recipientColors = {
    1: {
      main: '#1976d2',    // 蓝色
      light: '#e3f2fd',
    },
    2: {
      main: '#2e7d32',    // 绿色
      light: '#e8f5e9',
    },
    3: {
      main: '#ed6c02',    // 橙色
      light: '#fff3e0',
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSend = () => {
    navigate('/signature');
  };

  // 处理字段放置
  const handleFieldDrop = (item, position) => {
    if (!selectedRecipient) return;

    setPlacedFields([
      ...placedFields,
      {
        id: Date.now(),
        type: item.type,
        label: item.label,
        position: {
          x: position.x,
          y: position.y
        },
        recipientId: selectedRecipient.id,
        color: recipientColors[selectedRecipient.id].main
      }
    ]);
  };

  // 添加删除字段的处理函数
  const handleDeleteField = (fieldId) => {
    setPlacedFields(fields => fields.filter(field => field.id !== fieldId));
  };

  // 文档预览区域组件
  const DocumentPreview = () => {
    const [, drop] = useDrop({
      accept: ['field', 'placedField'],
      drop: (item, monitor) => {
        const offset = monitor.getClientOffset();
        const element = document.getElementById('document-preview');
        const rect = element.getBoundingClientRect();
        const position = {
          x: offset.x - rect.left,
          y: offset.y - rect.top
        };

        if (item.id) {
          // 更新已存在字段的位置
          setPlacedFields(fields => 
            fields.map(field => 
              field.id === item.id 
                ? { ...field, position }
                : field
            )
          );
        } else {
          // 添加新字段
          handleFieldDrop(item, position);
        }
      }
    });

    return (
      <Box
        sx={{ 
          display: 'flex',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 250px)',
          backgroundColor: '#f5f5f5',
          p: 4,
          overflow: 'auto'
        }}
      >
        <Box
          id="document-preview"
          ref={drop}
          sx={{ 
            position: 'relative',
            backgroundColor: '#fff',
            width: '210mm',           // A4 宽度
            minHeight: '297mm',       // A4 高度
            padding: '25.4mm',        // 1 inch margins
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            margin: '0 auto',
            '@media print': {
              boxShadow: 'none',
            }
          }}
        >
          {/* 显示文档内容 */}
          <Typography 
            component="pre" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '11pt',       // 标准文档字体大小
              lineHeight: 1.5,
            }}
          >
            {selectedDocument.content}
          </Typography>

          {/* 显示已放置的字段 */}
          {placedFields.map((field) => (
            <PlacedField 
              key={field.id} 
              field={field}
              onDelete={handleDeleteField}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Container maxWidth="xl">
        {/* Hero Section */}
        <Box sx={{ 
          pt: 4,
          pb: 3,
          textAlign: 'center',
          position: 'relative',
          borderBottom: '1px solid #e0e0e0',
          mb: 4
        }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Back
          </Button>

          <Typography 
            variant="h4" 
            sx={{ 
              color: '#1976d2',
              fontWeight: 600,
              mb: 1
            }}
          >
            {transactionData.name}
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#666',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Add signature positions for each recipient
          </Typography>

          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSend}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            Send for Signature
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Left Sidebar */}
          <Grid item xs={3}>
            {/* Recipients */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Recipients
              </Typography>
              <List>
                {recipients.map((recipient) => (
                  <ListItem 
                    key={recipient.id}
                    button
                    selected={selectedRecipient?.id === recipient.id}
                    onClick={() => setSelectedRecipient(recipient)}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: selectedRecipient?.id === recipient.id 
                        ? recipientColors[recipient.id].light 
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: recipientColors[recipient.id].light,
                      },
                      '&.Mui-selected': {
                        backgroundColor: recipientColors[recipient.id].light,
                        '&:hover': {
                          backgroundColor: recipientColors[recipient.id].light,
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: '100%',
                        backgroundColor: recipientColors[recipient.id].main,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        borderTopLeftRadius: 4,
                        borderBottomLeftRadius: 4,
                      }}
                    />
                    <ListItemText
                      primary={
                        <Typography 
                          sx={{ 
                            color: recipientColors[recipient.id].main,
                            fontWeight: selectedRecipient?.id === recipient.id ? 600 : 400
                          }}
                        >
                          {recipient.name}
                        </Typography>
                      }
                      secondary={recipient.role}
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Signature Fields */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Fields
              </Typography>
              {selectedRecipient ? (
                <>
                  <DraggableField 
                    type="signature" 
                    icon={SignatureIcon} 
                    label="Signer Signature"
                    color={recipientColors[selectedRecipient.id].main}
                  />
                  <DraggableField 
                    type="initials" 
                    icon={InitialsIcon} 
                    label="Signer Initials"
                    color={recipientColors[selectedRecipient.id].main}
                  />
                  <DraggableField 
                    type="name" 
                    icon={PersonIcon} 
                    label="Signer Name"
                    color={recipientColors[selectedRecipient.id].main}
                  />
                  <DraggableField 
                    type="date" 
                    icon={DateIcon} 
                    label="Signing Date"
                    color={recipientColors[selectedRecipient.id].main}
                  />
                  <DraggableField 
                    type="text" 
                    icon={TextIcon} 
                    label="Text Field"
                    color={recipientColors[selectedRecipient.id].main}
                  />
                </>
              ) : (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ textAlign: 'center' }}
                >
                  Please select a recipient to add fields
                </Typography>
              )}
            </Paper>

            {/* Documents List */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Documents
              </Typography>
              <List>
                {documents.map((doc) => (
                  <ListItem 
                    key={doc.id}
                    button
                    selected={selectedDocument.id === doc.id}
                    onClick={() => setSelectedDocument(doc)}
                  >
                    <ListItemIcon>
                      <DescriptionIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={doc.name} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Document Preview Area */}
          <Grid item xs={9}>
            <Paper 
              sx={{ 
                height: 'calc(100vh - 180px)',  // 调整整体高度
                backgroundColor: '#f5f5f5',
                overflow: 'auto',               // 允许滚动
              }}
            >
              <DocumentPreview />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </DndProvider>
  );
};

export default SignaturePositionPage; 